import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import ICONS from "../../../../assets/Icons";

import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";

import StatusContainer from "../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import FormatAmount from "../../../../components/_utils/FormatAmount/FormatAmount";

import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import TEXT from "../../../../utils/globalText";

/**
 * TestAuthMatrixOutcomePage - Displays the result of a test authorization matrix evaluation.
 *
 * This component renders the outcome page after a user tests an authorization matrix configuration.
 * It handles three distinct states:
 * - success: Transaction approved (all rule requirements matched)
 * - error: Transaction failed (selected user not in any matrix)
 * - partial: Transaction approved by selected user but further approvals pending
 *
 * The component receives its data via React Router's location state, which includes:
 * - page: The outcome state ('success', 'error', 'partial')
 * - selectedUser: The user who was tested
 * - selectedPreviousUsers: Array of previous approvers in the chain
 * - transaction_auth_matrices: Matrix configuration that was evaluated
 * - predicted_approval: Predicted approval grades for pending states
 *
 * @returns {JSX.Element} Rendered outcome page with status, approver details, and rule information
 */

/**
 * Helper to construct full name from user object.
 * Safely handles missing first/last name by filtering falsy values.
 */
const getFullName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ");

const getCardColors = (page) => {
  if (page === TEXT?.SUCCESS.toLowerCase()) {
    return {
      bgColor: "var(--positive-card)",
      borderColor: "var(--positive-bg)",
    };
  }

  if (page === TEXT?.ERROR.toLowerCase()) {
    return {
      bgColor: "var(--negative-card)",
      borderColor: "var(--negative-bg)",
    };
  }

  return {
    bgColor: "var(--neutral-card)",
    borderColor: "var(--neutral-bg)",
  };
};

const TestAuthMatrixOutcomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    page,
    selectedUser,
    selectedPreviousUsers,
    transaction_auth_matrices,
    predicted_approval,
  } = state || {};

  const { bgColor, borderColor } = getCardColors(page);

  // Determine status string based on page state for StatusContainer component
  const status =
    page === TEXT?.SUCCESS.toLowerCase()
      ? TEXT?.SUCCESS.toLowerCase()
      : page === TEXT?.ERROR.toLowerCase()
        ? TEXT?.ERROR.toLowerCase()
        : page === "partial"
          ? TEXT?.PENDING.toLowerCase()
          : "";

  const [rulePassedCount, setRulePassedCount] = useState([]);

  // Button configuration for the status container - dynamic colors based on status
  const buttonRows = [
    [
      {
        btnText: `${TEXT?.TEST} ${TEXT?.NEW.toLowerCase()}`,
        enabledBgColor: "var(--text-primary)",
        enabledTextColor:
          status === TEXT?.SUCCESS.toLowerCase()
            ? "var(--positive-bg)"
            : status === TEXT?.ERROR.toLowerCase()
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: () => {
          navigate("/settings/test_authorization_matrix");
        },
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor:
          status === TEXT?.SUCCESS.toLowerCase()
            ? "var(--positive-bg)"
            : status === TEXT?.ERROR.toLowerCase()
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
      },
    ],
    [
      {
        btnText: TEXT?.GO_TO_DASHBOARD,
        enabledBgColor:
          status === TEXT?.SUCCESS.toLowerCase()
            ? "var(--positive-bg)"
            : status === TEXT?.ERROR.toLowerCase()
              ? "var(--negative-bg)"
              : "var(--neutral-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () => {
          navigate("/home");
        },
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: TEXT?.DARK.toLowerCase(),
      },
    ],
  ];

  /**
   * Computes dynamic account/grade display data based on page state.
   *
   * For 'success' page: Uses rulePassedCount (populated from transaction_auth_matrices definition)
   * to show each role/grade and the count of approvals required.
   *
   * For 'partial' page: Uses predicted_approval.predicted_approval_grades to show
   * which grades are still pending approval.
   *
   * For 'error' page: Returns empty array (handled by conditional rendering in JSX).
   *
   */
  const dynamicAccounts =
    page === TEXT?.SUCCESS.toLowerCase()
      ? rulePassedCount &&
        rulePassedCount.length > GlobalConfiguration?.lengthCheckZero
        ? rulePassedCount.map((val) => ({
            leftTitle: val?.role,
            leftSubTitle: TEXT?.GRADE,
            rightText: true,
            rightTitle: val?.count,
            rightSubTitle: TEXT?.APPROVALS,
            rightTitleTextColor: "var(--text-primary)",
            rightTextFontWeight: "normal",
          }))
        : []
      : predicted_approval?.predicted_approval_grades &&
          predicted_approval.predicted_approval_grades.length >
            GlobalConfiguration?.lengthCheckZero
        ? predicted_approval.predicted_approval_grades.map((val, index) => ({
            leftTitle: val,
            leftSubTitle: TEXT?.GRADE,
            showBorder:
              predicted_approval.predicted_approval_grades.length -
                GlobalConfiguration?.lengthCheckOne !==
              index,
          }))
        : [];

  /**
   * Redirect to home if page state is missing (invalid navigation).
   * Runs once on mount since dependency array is empty.
   */
  useEffect(() => {
    if (!state?.page) {
      navigate("/home", { replace: true });
    }
  }, []);

  /**
   * Manages breadcrumb visibility and parses rule definition for success state.
   *
   * On mount: Hides breadcrumb and parses transaction_auth_matrices definition
   * to extract role-count pairs for the 'success' page display.
   *
   * The definition string format: "role1~count1<->role2~count2<->..."
   * Example: "G1~2<->G2~1" means Grade 1 needs 2 approvals, Grade 2 needs 1.
   *
   * Cleanup: Restores breadcrumb visibility on unmount.
   */
  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));
    if (page === TEXT?.SUCCESS.toLowerCase() && transaction_auth_matrices) {
      const definition =
        transaction_auth_matrices?.[GlobalConfiguration?.lengthCheckZero]
          ?.definition;

      setRulePassedCount(
        definition.split("<->").map((item) => {
          const [role, count] = item.split("~");
          return {
            role,
            count: Number(count),
          };
        }),
      );
    }
    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  return (
    <>
      <StatusContainer
        status={status}
        // Dynamic title based on page state
        title={
          page === TEXT?.ERROR.toLowerCase()
            ? `${TEXT?.TRANSACTION} ${TEXT?.APPROVAL.toLowerCase()} ${TEXT?.FAILED.toLowerCase()}`
            : page === TEXT?.SUCCESS.toLowerCase()
              ? `${TEXT?.TRANSACTION} ${TEXT?.APPROVAL.toLowerCase()} ${TEXT?.SUCCESS.toLowerCase()}ful`
              : page === "partial"
                ? `${TEXT?.TRANSACTION} ${TEXT?.APPROVAL.toLowerCase()} ${TEXT?.PENDING.toLowerCase()}`
                : ""
        }
        // Dynamic subtitle with context-specific messaging
        subtitle={
          page === TEXT?.ERROR.toLowerCase() ? (
            TEXT?.TRANSACTION_APPROVED_FAILED
          ) : page === TEXT?.SUCCESS.toLowerCase() ? (
            <>
              <div className="text-font-sm">
                {TEXT?.TRANSACTION_APPROVED_SUCCESSFULLY}
              </div>
            </>
          ) : page === "partial" ? (
            <>{TEXT?.TRANSACTION_APPROVED_PENDING_OTHERS}</>
          ) : (
            ""
          )
        }
        buttonRows={buttonRows}
      >
        {/* ============================================================
           RENDER BRANCH 1: ERROR STATE
           Shows selected user and previous approvers with error styling.
           Only rendered when page === "error"
        ============================================================ */}
        {page === TEXT?.ERROR.toLowerCase() ? (
          <TabsWeapperSection title={TEXT?.APPROVED_BY}>
            <>
              <InfoCard
                bgColor={bgColor}
                header={true}
                rightHeaderText={`${TEXT?.SELECTED} ${TEXT?.USER.toLowerCase()}`}
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                rightHeaderFontWeight="normal"
                leftHeaderText={getFullName(selectedUser)}
                leftHeaderTextColor="var(--text-secondary)"
                leftHeaderTextSize="var(--font-xs)"
                borderColor={borderColor}
                rows={[
                  {
                    rightTitle: selectedUser?.grade,
                    rightSubTitle: TEXT?.GRADE,
                    rightText: true,
                    rightTitleTextColor: "var(--text-primary)",
                    leftTitle: selectedUser?.user_id,
                    leftSubTitle: TEXT?.USER_ID,
                    leftTitleTextColor: "var(--text-primary)",
                    rightTextFontWeight: "normal",
                  },
                ]}
              />

              {selectedPreviousUsers?.map((val, index) => {
                return (
                  <InfoCard
                    bgColor={bgColor}
                    header={true}
                    rightHeaderText={TEXT?.PREVIOUS_APPROVER}
                    rightHeaderFontWeight="normal"
                    rightHeaderTextSize="var(--font-xs)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderText={getFullName(val)}
                    leftHeaderTextColor="var(--text-secondary)"
                    leftHeaderTextSize="var(--font-xs)"
                    borderColor={borderColor}
                    rows={[
                      {
                        rightTitle: val?.grade,
                        rightSubTitle: TEXT?.GRADE,
                        rightText: true,
                        leftText: true,
                        leftTitleTextColor: "var(--text-primary)",
                        leftTitle: val?.user_id,
                        leftSubTitle: TEXT?.USER_ID.toLowerCase(),
                        rightTitleTextColor: "var(--text-primary)",
                        leftTextFontWeight: "normal",
                      },
                    ]}
                  />
                );
              })}
            </>
          </TabsWeapperSection>
        ) : /* ============================================================
           RENDER BRANCH 2: PARTIAL/SUCCESS WITH NO GRADE DATA
           Shows only approver info (selected user + previous approvers)
           when there are no predicted grades (partial) or no rule data (success).
           Condition: (partial && no predicted grades) || (success && no rule data)
        ============================================================ */
        (page === "partial" &&
            !predicted_approval?.predicted_approval_grades &&
            !predicted_approval?.predicted_approval_grades?.length >
              GlobalConfiguration?.lengthCheckZero) ||
          (page === TEXT?.SUCCESS.toLowerCase() &&
            !rulePassedCount &&
            !rulePassedCount?.length > GlobalConfiguration?.lengthCheckZero) ? (
          <TabsWeapperSection title={TEXT?.APPROVED_BY}>
            <>
              <InfoCard
                bgColor={bgColor}
                header={true}
                rightHeaderText={`${TEXT?.SELECTED} ${TEXT?.USER.toLowerCase()}`}
                rightHeaderFontWeight="normal"
                rightHeaderTextSize="var(--font-xs)"
                rightHeaderTextColor="var(--text-secondary)"
                leftHeaderText={getFullName(selectedUser)}
                leftHeaderTextColor="var(--text-secondary)"
                leftHeaderTextSize="var(--font-xs)"
                borderColor={borderColor}
                rows={[
                  {
                    rightTitle: selectedUser?.grade,
                    rightSubTitle: TEXT?.GRADE,
                    rightText: true,
                    rightTitleTextColor: "var(--text-primary)",
                    leftText: true,
                    leftTitle: selectedUser?.user_id,
                    leftSubTitle: TEXT?.USER_ID.toLowerCase(),
                    leftTitleTextColor: "var(--text-primary)",
                    leftTextFontWeight: "normal",
                  },
                ]}
              />

              {selectedPreviousUsers?.map((val, index) => (
                <InfoCard
                  bgColor={bgColor}
                  header={true}
                  rightHeaderText={TEXT?.PREVIOUS_APPROVER}
                  rightHeaderFontWeight="normal"
                  rightHeaderTextSize="var(--font-xs)"
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={getFullName(val)}
                  leftHeaderTextColor="var(--text-secondary)"
                  leftHeaderTextSize="var(--font-xs)"
                  borderColor={borderColor}
                  rows={[
                    {
                      rightTitle: val?.grade,
                      rightSubTitle: TEXT?.GRADE,
                      rightText: true,
                      rightTitleTextColor: "var(--text-primary)",
                      leftText: true,
                      leftTitle: val?.user_id,
                      leftSubTitle: TEXT?.USER_ID.toLowerCase(),
                      leftTitleTextColor: "var(--text-primary)",
                      leftTextFontWeight: "normal",
                    },
                  ]}
                />
              ))}
            </>
          </TabsWeapperSection>
        ) : (
          /* ============================================================
             RENDER BRANCH 3: SUCCESS/PARTIAL WITH GRADE DATA
             Shows two tab sections:
             1. "Approved by" - Selected user + previous approvers (conditionally shown)
             2. "Passed By" / "Pending Approval/s" - Rule details with dynamic accounts
          ============================================================ */
          <>
            <TabsWeapperSection title={TEXT?.APPROVED_BY}>
              {/*
                 Conditionally render approver info:
                 - Always show for error state
                 - Show for success/partial when selectedTab === "approve"
              */}
              <>
                <InfoCard
                  bgColor={bgColor}
                  header={true}
                  rightHeaderText={`${TEXT?.SELECTED} ${TEXT?.USER.toLowerCase()}`}
                  rightHeaderFontWeight="normal"
                  rightHeaderTextSize="var(--font-xs)"
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={getFullName(selectedUser)}
                  leftHeaderTextColor="var(--text-secondary)"
                  leftHeaderTextSize="var(--font-xs)"
                  borderColor={borderColor}
                  rows={[
                    {
                      rightTitle: selectedUser?.grade,
                      rightSubTitle: TEXT?.GRADE,
                      rightText: true,
                      rightTitleTextColor: "var(--text-primary)",
                      leftText: true,
                      leftTitle: selectedUser?.user_id,
                      leftSubTitle: TEXT?.USER_ID.toLowerCase(),
                      leftTitleTextColor: "var(--text-primary)",
                      leftTextFontWeight: "normal",
                    },
                  ]}
                />

                {selectedPreviousUsers?.map((val, index) => (
                  <InfoCard
                    bgColor={bgColor}
                    header={true}
                    rightHeaderText={TEXT?.PREVIOUS_APPROVER}
                    rightHeaderFontWeight="normal"
                    rightHeaderTextSize="var(--font-xs)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderText={getFullName(val)}
                    leftHeaderTextColor="var(--text-secondary)"
                    leftHeaderTextSize="var(--font-xs)"
                    borderColor={borderColor}
                    rows={[
                      {
                        rightTitle: val?.grade,
                        rightSubTitle: TEXT?.GRADE,
                        rightText: true,
                        rightTitleTextColor: "var(--text-primary)",
                        leftText: true,
                        leftTitle: val?.user_id,
                        leftSubTitle: TEXT?.USER_ID.toLowerCase(),
                        leftTitleTextColor: "var(--text-primary)",
                        leftTextFontWeight: "normal",
                      },
                    ]}
                  />
                ))}
              </>
            </TabsWeapperSection>

            {/*
               Second tab section: Shows rule details for success,
               or pending approval grades for partial state
            */}
            <TabsWeapperSection
              title={
                page === TEXT?.SUCCESS.toLowerCase()
                  ? TEXT?.PASSED_BY
                  : TEXT?.PENDING_APPROVALS
              }
            >
              <>
                <div
                  // Disable ticker animation for non-success states
                  className={
                    page === TEXT?.SUCCESS.toLowerCase()
                      ? ""
                      : "[&_.whitespace-nowrap]:whitespace-normal [&_.ticker-text]:block [&_.ticker-text]:whitespace-normal [&_.ticker-text]:!animate-none [&_.ticker-text]:[transform:none]"
                  }
                >
                  <InfoCard
                    bgColor={
                      page === TEXT?.SUCCESS.toLowerCase()
                        ? "var(--positive-card)"
                        : "var(--neutral-card)"
                    }
                    header={true}
                    // Left header: "Rule" for success, descriptive text for partial
                    leftHeaderText={
                      page === TEXT?.SUCCESS.toLowerCase()
                        ? TEXT?.RULE
                        : TEXT?.APPROVALS_PENDING_FROM_GRADES
                    }
                    leftHeaderFontWeight="normal"
                    // Right header: Amount range for success, empty for partial
                    rightHeaderText={
                      page === TEXT?.SUCCESS.toLowerCase() ? (
                        <>
                          ₹{" "}
                          <FormatAmount
                            price={
                              transaction_auth_matrices?.[
                                GlobalConfiguration?.lengthCheckZero
                              ]?.lower_amount
                            }
                          />{" "}
                          🡢 ₹{" "}
                          <FormatAmount
                            price={
                              transaction_auth_matrices?.[
                                GlobalConfiguration?.lengthCheckZero
                              ]?.higher_amount
                            }
                          />
                        </>
                      ) : (
                        ""
                      )
                    }
                    rightHeaderTextColor="var(--text-secondary)"
                    rightHeaderTextSize="var(--font-xs)"
                    borderColor={
                      page === TEXT?.SUCCESS.toLowerCase()
                        ? "var(--positive-bg)"
                        : "var(--neutral-bg)"
                    }
                    // dynamicAccounts computed earlier based on page state
                    rows={dynamicAccounts}
                  />
                </div>
              </>
            </TabsWeapperSection>
          </>
        )}
      </StatusContainer>
    </>
  );
};

export default TestAuthMatrixOutcomePage;
